import { useLocation } from 'react-router'
import { Box, Typography } from '@mui/material'
import { useIntl } from 'react-intl'
import { RecoveryFlowState } from '@ory/client'

import { tokens } from '@configuration/theme'
import InlineMessage from '@base/InlineMessage/InlineMessage.component'
import TextField from '@base/TextField/TextField.component'
import MailIcon from '@icons/Mail.icon'
import LockIcon from '@icons/Lock.icon'
import AuthError from '@components/AuthError/AuthError.component'
import AuthLoading from '@components/AuthLoading/AuthLoading.component'
import SplitAuthLayout from '@components/SplitAuthLayout/SplitAuthLayout.component'
import SubmitRow from '@components/SubmitRow/SubmitRow.component'
import BackButton from '@components/BackButton'
import { markRecoveryJustCompleted, stashRecoveryReturnTo } from '@utils/recovery-handoff.utils'
import { translateKratosText } from '@i18n/kratos/kratos-i18n'
import {
  isNonScriptUrl,
  isSafePath,
  isSameOriginFormAction,
  isTrustedReturnTo,
} from '@utils/url.utils'
import {
  findFieldError,
  findInput,
  findSubmitMethod,
  getFlowMessages,
} from '@utils/kratos-nodes.utils'

import { useRecoveryFlow } from './hooks/useRecoveryFlow'
import { useResendCooldown } from './hooks/useResendCooldown'

const RecoveryPage = () => {
  const intl = useIntl()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const flowId = params.get('flow')
  // MIGRATION: remove once every pre-4.5.2 invitation has expired — seven days
  // after 4.5.2 reaches prod, not seven days from when this was written (prod
  // is still minting the old links). Those mails point here with the code in
  // the query and nothing in the body; their flows are admin-issued, so they
  // cannot resend and the invitee would have no way forward at all.
  // Prefill only — this page must never submit a code it did not receive from
  // a person, which is what let scanners burn them.
  const legacyInviteCode = params.get('code')
  const rawReturn = params.get('return_to')
  const backHref = rawReturn && isSafePath(rawReturn) ? rawReturn : '/login'

  // Forward return_to into Kratos so it lands the user back where they came
  // from after the flow completes (recovery → settings → redirect). Wider than
  // `backHref`'s check on purpose: the product lives on another paretos.com
  // subdomain, and this hop is where an absolute target has to survive. Kratos
  // re-checks it against allowed_return_urls and 400s on anything else.
  const initUrl =
    rawReturn && isTrustedReturnTo(rawReturn)
      ? `/self-service/recovery/browser?return_to=${encodeURIComponent(rawReturn)}`
      : '/self-service/recovery/browser'

  const { flow, error, loading } = useRecoveryFlow(flowId, initUrl)
  const { resendRemaining, handleResendSubmit } = useResendCooldown(flow)

  const errorWelcome = intl.formatMessage({ id: 'recovery.error.welcome' })

  if (loading) {
    return <AuthLoading />
  }

  if (error || !flow) {
    return (
      <AuthError
        welcome={errorWelcome}
        message={error || intl.formatMessage({ id: 'recovery.expired' })}
      />
    )
  }

  const nodes = flow.ui.nodes
  const csrfAttrs = findInput(nodes, 'csrf_token')
  const emailAttrs = findInput(nodes, 'email')
  const codeAttrs = findInput(nodes, 'code')
  const methodAttrs = findSubmitMethod(nodes)
  const codeError = findFieldError(nodes, 'code', intl)
  const emailError = findFieldError(nodes, 'email', intl)

  // Admin-issued recovery flows (customer-api welcome mail) come from
  // /admin/recovery/code without a browser-bound session, so Kratos doesn't
  // emit csrf_token or email nodes — only `code` + `method=code`. The verify
  // submit still works without csrf for these flows; the resend form does
  // not (no email to post) and is hidden below.
  if (!methodAttrs) {
    return (
      <AuthError
        welcome={errorWelcome}
        message={intl.formatMessage({ id: 'recovery.error.missingFields' })}
      />
    )
  }
  if (!isSameOriginFormAction(flow.ui.action)) {
    return (
      <AuthError
        welcome={errorWelcome}
        message={intl.formatMessage({ id: 'recovery.error.formActionUnexpectedOrigin' })}
      />
    )
  }

  const csrfValue = csrfAttrs ? String(csrfAttrs.value ?? '') : null
  const methodValue = String(methodAttrs.value ?? '')
  const flowMessages = getFlowMessages(flow)
  const inCodeStage = flow.state === RecoveryFlowState.SentEmail

  // Kratos contradicting itself: surface as error, not silent fallback.
  if (inCodeStage && !codeAttrs) {
    return (
      <AuthError
        welcome={errorWelcome}
        message={intl.formatMessage({ id: 'recovery.error.unexpectedState' })}
      />
    )
  }

  const welcomeKey = inCodeStage ? 'recovery.codeStage.welcome' : 'recovery.emailStage.welcome'
  const submitKey = inCodeStage ? 'recovery.submit.verify' : 'recovery.submit.send'

  return (
    <SplitAuthLayout
      heading={intl.formatMessage({ id: welcomeKey })}
      subheading={!inCodeStage ? intl.formatMessage({ id: 'recovery.emailStage.intro' }) : ''}
      dataTestId='recovery-page'
      back={
        <BackButton to={backHref}>{intl.formatMessage({ id: 'recovery.backToLogin' })}</BackButton>
      }
    >
      {flowMessages.map((m) => (
        <InlineMessage key={m.id} variant={m.type === 'error' ? 'error' : 'info'}>
          {translateKratosText(m, intl)}
        </InlineMessage>
      ))}
      <Box
        component='form'
        action={flow.ui.action}
        method={flow.ui.method}
        sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}
        // Only the verify-code submission progresses to /settings; the
        // email-stage submission keeps the user on /recovery. Setting the
        // handoff flag only in code stage prevents a stray Password-tab
        // default if the user later navigates to settings from elsewhere.
        // return_to handoff: see stashRecoveryReturnTo in recovery-handoff.ts.
        onSubmit={
          inCodeStage
            ? () => {
                markRecoveryJustCompleted()
                if (rawReturn && isNonScriptUrl(rawReturn)) {
                  stashRecoveryReturnTo(rawReturn)
                }
              }
            : undefined
        }
      >
        {csrfValue !== null && <input type='hidden' name='csrf_token' value={csrfValue} />}
        {/* Carry `method` on a hidden input rather than the submit button:
            SubmitRow renders the button without name/value, so the
            discriminator would otherwise be missing and Kratos would reject
            the body. */}
        <input type='hidden' name='method' value={methodValue} />
        {inCodeStage ? (
          // No email field: with it, Kratos always takes the resend path.
          <TextField
            name='code'
            type='text'
            icon={<LockIcon />}
            placeholder={intl.formatMessage({ id: 'recovery.field.code.placeholder' })}
            label={intl.formatMessage({ id: 'recovery.field.code.label' })}
            autoComplete='one-time-code'
            inputMode='numeric'
            autoFocus={true}
            required={true}
            defaultValue={String(codeAttrs?.value ?? '') || legacyInviteCode || ''}
            error={codeError}
          />
        ) : (
          <TextField
            name='email'
            type='email'
            icon={<MailIcon />}
            placeholder={intl.formatMessage({ id: 'recovery.field.email.placeholder' })}
            label={intl.formatMessage({ id: 'recovery.field.email.label' })}
            autoComplete='email'
            autoFocus={true}
            required={true}
            defaultValue={String(emailAttrs?.value ?? '')}
            error={emailError}
          />
        )}
        <SubmitRow submitLabel={intl.formatMessage({ id: submitKey })} />
      </Box>
      {inCodeStage && csrfValue !== null && emailAttrs && (
        // Separate form: posting email without code makes Kratos take the
        // resend path. Nested forms are illegal HTML, so this sits next to
        // the verify form rather than inside it. Admin-issued flows omit
        // csrf + email nodes, so resend is not viable for them — the user
        // would have to restart from /login if their code expires.
        <Box
          component='form'
          action={flow.ui.action}
          method={flow.ui.method}
          onSubmit={handleResendSubmit}
          sx={{
            width: '100%',
            textAlign: 'center',
            mt: 0,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            gap: 0.5,
          }}
        >
          <input type='hidden' name='csrf_token' value={csrfValue} />
          <input type='hidden' name='email' value={String(emailAttrs.value ?? '')} />
          <input type='hidden' name='method' value={methodValue} />

          <Typography variant='body2'>
            {intl.formatMessage({ id: 'recovery.resend.didntGet' })}
          </Typography>
          <Typography
            component='button'
            variant='body2'
            type='submit'
            disabled={resendRemaining > 0}
            sx={{
              background: 'none',
              border: 0,
              padding: 0,
              textDecoration: 'underline',
              color: resendRemaining > 0 ? tokens.businessBlack60 : tokens.versatileViolet,
              cursor: resendRemaining > 0 ? 'default' : 'pointer',
              '&:focus-visible': {
                outline: `2px solid ${tokens.versatileViolet}`,
                outlineOffset: 2,
              },
            }}
          >
            {resendRemaining > 0
              ? intl.formatMessage({ id: 'recovery.resend.cooldown' }, { seconds: resendRemaining })
              : intl.formatMessage({ id: 'recovery.resend.idle' })}
          </Typography>
        </Box>
      )}
    </SplitAuthLayout>
  )
}

export default RecoveryPage
